<script lang="ts">
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import { acquisitionReferralFormClientSchema } from "@fergmar/contracts/public/forms/acquisition-referral-form.schema";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import {
		PlacesAutoCompleteInput,
		VICTORIA_SERVICE_BOUNDS
	} from "#lib/web-ui/ui/places-input/index.js";
	import { PUBLIC_GOOGLE_PLACES_TOKEN } from "$app/env/public";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Textarea } from "#lib/web-ui/ui/textarea/index.js";
	import { toast } from "svelte-sonner";
	import { dev } from "$app/env";
	import { page } from "$app/state";
	import CheckCircleIcon from "@lucide/svelte/icons/check-circle";
	import AlertCircleIcon from "@lucide/svelte/icons/alert-circle";
	import CameraIcon from "@lucide/svelte/icons/camera";
	import MapPinIcon from "@lucide/svelte/icons/map-pin";
	import { cn } from "#lib/web-ui/ui/utils.js";
	import {
		AcquisitionReferralStatusEnum,
		type StagedReferralPhoto
	} from "@fergmar/contracts/public/referrals";
	import { v4 as uuidv4 } from "uuid";
	import { appConfig } from "#lib/app-config.js";
	import { PublicFileUploader } from "#lib/web-ui/file-uploader/index.js";
	import { TurnstileField } from "#lib/web-ui/forms/turnstile/index.js";

	let referralId = $state(uuidv4());
	let stagedPhotos = $state<StagedReferralPhoto[]>([]);
	let uploadSuccessCount = $state(0);
	let uploadPendingCount = $state(0);
	let uploadingCount = $state(0);
	let uploadErrorCount = $state(0);
	let showPhotoError = $state(false);

	const photoBlockReason = $derived.by(() => {
		if (uploadingCount > 0 || uploadPendingCount > 0)
			return "Wait for uploads to finish before submitting.";
		if (uploadErrorCount > 0) return "Retry or remove failed uploads before submitting.";
		if (uploadSuccessCount < 1) return "At least 1 photo is required.";
		return null;
	});

	function pushToDataLayer(eventName: string) {
		if (typeof window !== "undefined" && window.dataLayer) {
			window.dataLayer.push({
				event: eventName,
				formId: "referral_form",
				pagePath: window.location.pathname
			});
		}
	}

	const form = superForm(defaults(zod4(acquisitionReferralFormClientSchema)), {
		id: "acquisition-referral",
		SPA: true,
		resetForm: false,
		validators: zod4(acquisitionReferralFormClientSchema),
		async onUpdate({ form, cancel }) {
			if (!form.valid) return;

			if (photoBlockReason) {
				showPhotoError = true;
				toast.error(photoBlockReason);
				cancel();
				return;
			}

			try {
				if (!form.data.addressComponents) return;
				await trpcBrowser.public.forms.submitAcquisitionReferral.mutate({
					...form.data,
					addressComponents: form.data.addressComponents,
					id: referralId,
					status: AcquisitionReferralStatusEnum.NewReferral,
					pageUrl: page.url.href,
					routeId: page.route.id ?? "",
					utmSource: page.url.searchParams.get("utm_source") ?? "",
					utmMedium: page.url.searchParams.get("utm_medium") ?? "",
					utmCampaign: page.url.searchParams.get("utm_campaign") ?? "",
					utmContent: page.url.searchParams.get("utm_content") ?? "",
					utmTerm: page.url.searchParams.get("utm_term") ?? "",
					photos: stagedPhotos
				});
			} catch (e) {
				form.data.turnstileToken = "";
				toast.error(
					e instanceof Error
						? e.message
						: "Failed to create acquisition referral. Please try again."
				);
				pushToDataLayer("form_submission_error");
				return;
			}

			toast.success("Referral submitted successfully!");
			pushToDataLayer("form_submission_success");
			resetForNewReferral();
		}
	});

	const { form: formData, enhance, errors, submitting } = form;

	function enhanceAttachment(element: HTMLFormElement) {
		const action = enhance(element);
		return () => action?.destroy?.();
	}

	function resetForNewReferral() {
		referralId = uuidv4();
		stagedPhotos = [];
		form.reset();
		showPhotoError = false;
	}
</script>

<form {@attach enhanceAttachment} method="POST" class="flex w-full flex-col space-y-6">
	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
		<div class="space-y-1">
			<Label class="text-sm font-medium text-gray-700">Name</Label>
			<Input
				name="referredBy"
				bind:value={$formData.referredBy}
				class={cn(
					"transition-all duration-200 focus:ring-2 focus:ring-green-500",
					$errors.referredBy
						? "border-red-300 focus:border-red-500 focus:ring-red-500"
						: "border-gray-300 focus:border-green-500"
				)}
			/>
			{#if $errors.referredBy}
				<p class="mt-1 flex items-center gap-1 text-xs text-red-600">
					<AlertCircleIcon class="h-3 w-3" />
					<span>{$errors.referredBy}</span>
				</p>
			{/if}
		</div>

		<div class="space-y-1">
			<Label class="text-sm font-medium text-gray-700">Email</Label>
			<Input
				name="referrerEmail"
				type="email"
				bind:value={$formData.referrerEmail}
				class={cn(
					"transition-all duration-200 focus:ring-2 focus:ring-green-500",
					$errors.referrerEmail
						? "border-red-300 focus:border-red-500 focus:ring-red-500"
						: "border-gray-300 focus:border-green-500"
				)}
			/>
			{#if $errors.referrerEmail}
				<p class="mt-1 flex items-center gap-1 text-xs text-red-600">
					<AlertCircleIcon class="h-3 w-3" />
					<span>{$errors.referrerEmail}</span>
				</p>
			{/if}
		</div>
	</div>

	<div class="border-primary/30 bg-primary/10 rounded-lg border p-3">
		<div class="flex items-start gap-2.5">
			<MapPinIcon class="text-primary mt-0.5 h-4 w-4 flex-shrink-0" />
			<div>
				<p class="text-primary text-sm font-semibold">Victoria & Crossroads Area Only</p>
				<p class="text-foreground/70 text-xs">
					Only properties in our service area will be reviewed for payout.
				</p>
			</div>
		</div>
	</div>

	<div class="space-y-1">
		<Label class="text-sm font-medium text-gray-700">Property Address</Label>
		<PlacesAutoCompleteInput
			name="address"
			bind:value={$formData.address}
			placesToken={PUBLIC_GOOGLE_PLACES_TOKEN}
			bounds={VICTORIA_SERVICE_BOUNDS}
			oninput={() => ($formData.addressComponents = null)}
			onPlaceSelect={(place) => {
				$formData.addressComponents = place.components;
				form.validate("address");
			}}
			class={cn(
				"transition-all duration-200 focus:ring-2 focus:ring-green-500",
				$errors.address
					? "border-red-300 focus:border-red-500 focus:ring-red-500"
					: "border-gray-300 focus:border-green-500"
			)}
		/>
		{#if $errors.address}
			<p class="mt-1 flex items-center gap-1 text-xs text-red-600">
				<AlertCircleIcon class="h-3 w-3" />
				<span>{$errors.address}</span>
			</p>
		{/if}
	</div>

	<div class="space-y-1">
		<Label class="text-sm font-medium text-gray-700">Details about the Property</Label>
		<Textarea
			name="notes"
			bind:value={$formData.notes}
			placeholder="Describe the condition: boarded windows, overgrown yard, needs roof repair, etc."
			rows={4}
			class={cn(
				"bg-background resize-none transition-all duration-200 focus:ring-2 focus:ring-green-500",
				$errors.notes
					? "border-red-300 focus:border-red-500 focus:ring-red-500"
					: "border-gray-300 focus:border-green-500"
			)}
		/>
		<div class="flex justify-between text-xs text-gray-500">
			<span
				class={cn(
					"font-medium transition-colors",
					($formData.notes?.length ?? 0) > 1000
						? "text-red-500"
						: ($formData.notes?.length ?? 0) > 800
							? "text-yellow-500"
							: ($formData.notes?.length ?? 0) >= 10
								? "text-green-500"
								: "text-gray-400"
				)}
			>
				{$formData.notes?.length ?? 0}/1000
			</span>
		</div>
		{#if $errors.notes}
			<p class="mt-1 flex items-center gap-1 text-xs text-red-600">
				<AlertCircleIcon class="h-3 w-3" />
				<span>{$errors.notes}</span>
			</p>
		{/if}
	</div>

	<div class="space-y-4">
		<div class="flex items-center gap-2">
			<Label class="text-sm font-medium text-gray-700">Property Photos</Label>
		</div>

		{#key referralId}
			<PublicFileUploader
				{referralId}
				accept="image/*,.heic,.heif"
				bind:successCount={uploadSuccessCount}
				bind:pendingCount={uploadPendingCount}
				bind:uploadingCount
				bind:errorCount={uploadErrorCount}
				bind:stagedPhotos
			/>
		{/key}

		{#if showPhotoError && photoBlockReason}
			<p class="flex items-center gap-1 text-xs text-red-600">
				<AlertCircleIcon class="h-3 w-3" />
				<span>{photoBlockReason}</span>
			</p>
		{:else if photoBlockReason && uploadSuccessCount + uploadPendingCount + uploadingCount > 0}
			<p class="text-xs text-gray-500">{photoBlockReason}</p>
		{/if}

		<div class="rounded border border-blue-200 bg-blue-50 p-3">
			<p class="flex items-start gap-2 text-xs text-blue-700">
				<CameraIcon class="mt-0.5 h-3 w-3 flex-shrink-0" />
				<span
					><strong>Photo Tips:</strong> Take clear photos showing exterior damage, overgrown
					landscaping, broken windows, peeling paint, or other signs of distress that indicate
					the property needs work.</span
				>
			</p>
		</div>
	</div>

	<div>
		<TurnstileField bind:token={$formData.turnstileToken} class="mb-4" />
	</div>

	<div class="space-y-3">
		<Button
			type="submit"
			disabled={$submitting || photoBlockReason !== null}
			class={cn(
				"h-14 w-full rounded-lg text-xl font-semibold",
				$submitting || photoBlockReason !== null
					? "cursor-not-allowed opacity-75"
					: "shadow-lg hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
			)}
		>
			{#if $submitting}
				<div class="flex items-center gap-2">
					<div
						class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
					></div>
					Submitting Referral...
				</div>
			{:else}
				<div class="flex items-center gap-2">
					<CheckCircleIcon class="h-4 w-4" />
					Submit Referral & Get Paid!
				</div>
			{/if}
		</Button>
	</div>
</form>

{#if dev && appConfig.displayFormDebug}
	<div class="mt-8 rounded bg-gray-100 p-4">
		<h4 class="text-foreground mb-2 font-medium">Debug Info</h4>
		<pre class="text-xs">{JSON.stringify(
				{
					id: referralId,
					referredBy: $formData.referredBy,
					referrerEmail: $formData.referrerEmail,
					address: $formData.address,
					notes: $formData.notes,
					status: AcquisitionReferralStatusEnum.NewReferral
				},
				null,
				2
			)}</pre>
	</div>
{/if}
